// Admin → Translations. DB overrides for static UI strings (motiflink §1.5).
//
// What this page edits is only HALF the i18n system, and the distinction is
// load-bearing:
//
//   ui.* app.* content.* notif* plan* badge.*   → TypeScript bundles, edited in
//                                                 code so the UI renders even
//                                                 when the DB is unreachable.
//   marketing.* lesson.* subject.* track.* unit.* → these rows, editable live.
//
// A row here overlays the bundle at boot. Deleting a bundle-backed row reveals
// the built-in string; deleting a DB-only row makes it render EMPTY, because
// motiflink §1.2 kills cross-language fallback. The delete dialog says which
// case you're in.
//
// The legacy translation_keys editor is at /translations-legacy until the M9
// cutover. Chrome is English-only by owner ruling (CLAUDE.md §5).

import { Button, Dialog, DialogContent, Input, Textarea } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { RowActions } from '~/components';
import { ApiError } from '~/lib/api';
import { parseTranslationsCsv, type CsvParseError } from '~/lib/csv';
import {
  deleteTranslation,
  importTranslations,
  listLanguages,
  listTranslations,
  translationsExportUrl,
  updateTranslation,
  upsertTranslation,
  type TranslationRow,
} from '~/lib/i18n';

const PAGE_SIZE = 50;

/** Namespaces owned by the TypeScript bundles rather than this table. */
const BUNDLE_PREFIXES = ['ui.', 'app.', 'content.', 'notif', 'plan', 'badge.'];

function isBundleKey(key: string): boolean {
  return BUNDLE_PREFIXES.some((p) => key.startsWith(p));
}

// Exported because the generated routeTree references this type by name in
// the route's search-params signature.
export interface Search {
  lang?: string;
  category?: string;
  q?: string;
  offset?: number;
}

export const Route = createFileRoute('/_authed/translations')({
  // Filters live in the URL so a filtered view is linkable and survives reload.
  validateSearch: (search: Record<string, unknown>): Search => ({
    lang: typeof search.lang === 'string' && search.lang ? search.lang : undefined,
    category: typeof search.category === 'string' && search.category ? search.category : undefined,
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
    offset: Number(search.offset) > 0 ? Number(search.offset) : undefined,
  }),
  component: TranslationsPage,
});

function TranslationsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const { lang, category, q, offset = 0 } = Route.useSearch();

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<TranslationRow | null>(null);
  const [importing, setImporting] = useState(false);

  // Local mirror of the search box so typing feels instant; the URL (and the
  // query) only follow after the debounce.
  const [searchDraft, setSearchDraft] = useState(q ?? '');
  useEffect(() => setSearchDraft(q ?? ''), [q]);

  const setSearch = (next: Partial<Search>) =>
    void navigate({
      search: (prev: Search) => ({ ...prev, offset: undefined, ...next }),
      replace: true,
    });

  useEffect(() => {
    const draft = searchDraft.trim();
    if (draft === (q ?? '')) return;
    const timer = setTimeout(() => setSearch({ q: draft || undefined }), 300);
    return () => clearTimeout(timer);
    // `setSearch` is recreated each render; depending on it would re-arm the
    // timer every keystroke and it would never fire.
    // NOTE: deliberately omitted from the dep array (see above).
    // eslint-plugin-react-hooks is not installed; restore the
    // `react-hooks/exhaustive-deps` disable directive when it is.
  }, [searchDraft, q]);

  const languages = useQuery({
    queryKey: ['i18n', 'languages'],
    queryFn: ({ signal }) => listLanguages(signal),
    staleTime: 60_000,
  });

  const listKey = ['i18n', 'translations', { lang, category, q, offset }] as const;
  const translations = useQuery({
    queryKey: listKey,
    queryFn: ({ signal }) =>
      listTranslations({ lang, category, q, limit: PAGE_SIZE, offset }, signal),
    staleTime: 10_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['i18n', 'translations'] });
  const onError = (e: unknown) =>
    setError(e instanceof ApiError ? e.message : 'Something went wrong.');

  const save = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      updateTranslation(id, { translationValue: value }),
    onSuccess: invalidate,
    onError,
  });

  const remove = useMutation({
    mutationFn: deleteTranslation,
    onSuccess: () => {
      setConfirmDelete(null);
      void invalidate();
    },
    onError,
  });

  const rows = translations.data?.rows ?? [];
  const total = translations.data?.total ?? 0;

  // Direction per language, so an Arabic value renders RTL inside the LTR admin.
  const dirByCode = useMemo(() => {
    const map = new Map<string, 'ltr' | 'rtl'>();
    for (const l of languages.data ?? []) map.set(l.code, l.direction);
    return map;
  }, [languages.data]);

  return (
    <div className="mx-auto max-w-6xl">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-admin-900">Translations</h1>
          <p className="mt-1 text-sm text-ink-700">
            Live-editable strings. Changes appear on the next navigation — no deploy needed.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setImporting(true)}>
            Import CSV
          </Button>
          {/* Plain anchor: the endpoint is cookie-authed and sets both the
              filename and the Excel-safe BOM. */}
          <a
            href={translationsExportUrl(lang)}
            className="inline-flex h-10 items-center rounded-2xl border border-admin-200 px-4 text-sm font-semibold text-admin-700 transition-colors hover:bg-admin-100"
          >
            Export CSV
          </a>
          <Button onClick={() => setCreating(true)}>Add string</Button>
        </div>
      </header>

      {error && (
        <p role="alert" className="mt-4 rounded-xl bg-heart/10 px-4 py-3 text-sm text-heart">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="mt-4 rounded-xl bg-leaf/10 px-4 py-3 text-sm text-leaf">
          {notice}
        </p>
      )}

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-end gap-3 rounded-2xl border border-burgundy-500/10 bg-white p-4">
        <label className="text-sm font-medium text-ink-700">
          Language
          <select
            value={lang ?? ''}
            onChange={(e) => setSearch({ lang: e.target.value || undefined })}
            className="mt-1 block h-10 rounded-2xl border border-burgundy-500/15 bg-cream-50 px-3 text-sm text-ink-900"
          >
            <option value="">All</option>
            {(languages.data ?? []).map((l) => (
              <option key={l.code} value={l.code}>
                {l.name} ({l.code})
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm font-medium text-ink-700">
          Category
          <Input
            value={category ?? ''}
            onChange={(e) => setSearch({ category: e.target.value || undefined })}
            placeholder="marketing"
            className="mt-1 h-10 w-44"
          />
        </label>

        <label className="flex-1 text-sm font-medium text-ink-700">
          Search
          <Input
            type="search"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Key or value…"
            className="mt-1 h-10 min-w-48"
          />
        </label>

        {(lang || category || q) && (
          <Button
            variant="ghost"
            onClick={() => void navigate({ search: {} as Search, replace: true })}
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-burgundy-500/10 bg-white">
        {translations.isLoading ? (
          <p className="p-6 text-sm text-ink-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-ink-500">
            No strings match these filters. {(lang || category || q) && 'Try clearing them.'}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-burgundy-500/10 bg-admin-50">
              <tr className="text-xs font-semibold uppercase tracking-wider text-admin-400">
                <th className="px-4 py-3 text-start">Key</th>
                <th className="w-20 px-4 py-3 text-start">Lang</th>
                <th className="px-4 py-3 text-start">Value</th>
                <th className="w-32 px-4 py-3 text-start">Category</th>
                <th className="w-24 px-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <TranslationTableRow
                  key={row.id}
                  row={row}
                  dir={dirByCode.get(row.languageCode) ?? 'ltr'}
                  saving={save.isPending && save.variables?.id === row.id}
                  onSave={(value) => save.mutateAsync({ id: row.id, value })}
                  onDelete={() => setConfirmDelete(row)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pager */}
      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm text-ink-500">
          <span>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() =>
                void navigate({
                  search: (p: Search) => ({ ...p, offset: Math.max(0, offset - PAGE_SIZE) }),
                })
              }
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() =>
                void navigate({ search: (p: Search) => ({ ...p, offset: offset + PAGE_SIZE }) })
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {creating && (
        <CreateStringDialog
          languages={(languages.data ?? []).map((l) => ({ code: l.code, name: l.name }))}
          defaultLang={lang}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            setNotice('String saved.');
            void invalidate();
          }}
          onError={onError}
        />
      )}

      {importing && (
        <ImportDialog
          onClose={() => setImporting(false)}
          onDone={(count) => {
            setImporting(false);
            setNotice(`Imported ${count} string${count === 1 ? '' : 's'}.`);
            void invalidate();
          }}
          onError={onError}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteDialog
          row={confirmDelete}
          pending={remove.isPending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => remove.mutate(confirmDelete.id)}
        />
      )}
    </div>
  );
}

// ─── one row, with inline editing ───────────────────────────────────────────

function TranslationTableRow({
  row,
  dir,
  saving,
  onSave,
  onDelete,
}: {
  row: TranslationRow;
  dir: 'ltr' | 'rtl';
  saving: boolean;
  onSave: (value: string) => Promise<unknown>;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.translationValue);

  function begin() {
    setDraft(row.translationValue);
    setEditing(true);
  }

  async function commit() {
    if (draft === row.translationValue) {
      setEditing(false);
      return;
    }
    await onSave(draft);
    setEditing(false);
  }

  return (
    <tr className="border-b border-burgundy-500/5 last:border-0 align-top">
      <td className="px-4 py-3">
        <span className="font-mono text-xs text-admin-900">{row.translationKey}</span>
        {isBundleKey(row.translationKey) && (
          <span
            className="ms-2 rounded-full bg-admin-100 px-2 py-0.5 text-[10px] font-semibold text-admin-500"
            title="This namespace also ships in the TypeScript bundle; this row overrides it."
          >
            bundle
          </span>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-xs uppercase text-ink-500">{row.languageCode}</td>
      <td className="px-4 py-3">
        {editing ? (
          <div>
            <Textarea
              autoFocus
              dir={dir}
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setEditing(false);
                // Ctrl/Cmd+Enter saves; plain Enter inserts a newline, since
                // these values legitimately contain them.
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void commit();
              }}
              className="text-sm"
            />
            <div className="mt-2 flex items-center gap-2">
              <Button size="sm" disabled={saving} onClick={() => void commit()}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <span className="text-xs text-ink-500">Esc to cancel · ⌘/Ctrl+Enter to save</span>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={begin}
            dir={dir}
            className="w-full rounded-lg px-2 py-1 text-start text-admin-900 transition-colors hover:bg-admin-50"
            title="Click to edit"
          >
            {row.translationValue || <span className="text-ink-500">(empty)</span>}
          </button>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-admin-500">{row.category ?? '—'}</td>
      <td className="px-4 py-3 text-end">
        {/* The unified row-action shape. This table keeps its hand-rolled
            markup because the VALUE cell is an inline editor — a DataTable
            migration would put that editing behaviour at risk for no gain. */}
        <RowActions
          label={row.translationKey}
          primary={{ id: 'edit', label: 'Edit value', icon: 'edit', onSelect: begin }}
          actions={[
            {
              id: 'copy-key',
              label: 'Copy key',
              icon: 'copy',
              onSelect: () => void navigator.clipboard?.writeText(row.translationKey),
            },
            {
              id: 'delete',
              label: 'Delete string',
              icon: 'trash',
              destructive: true,
              onSelect: onDelete,
            },
          ]}
        />
      </td>
    </tr>
  );
}

// ─── create ─────────────────────────────────────────────────────────────────

function CreateStringDialog({
  languages,
  defaultLang,
  onClose,
  onSaved,
  onError,
}: {
  languages: Array<{ code: string; name: string }>;
  defaultLang: string | undefined;
  onClose: () => void;
  onSaved: () => void;
  onError: (e: unknown) => void;
}) {
  const [key, setKey] = useState('');
  const [languageCode, setLanguageCode] = useState(defaultLang ?? languages[0]?.code ?? '');
  const [value, setValue] = useState('');
  const [category, setCategory] = useState('');

  const save = useMutation({
    mutationFn: () =>
      upsertTranslation({
        translationKey: key.trim(),
        languageCode,
        translationValue: value,
        category: category.trim() || null,
      }),
    onSuccess: onSaved,
    onError,
  });

  const trimmedKey = key.trim();
  const validKey = /^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)*$/.test(trimmedKey);
  const warnBundle = validKey && isBundleKey(trimmedKey);
  const dir = languages.find((l) => l.code === languageCode)?.code === 'ar' ? 'rtl' : 'ltr';

  return (
    <DialogShell title="Add string" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (validKey && languageCode) save.mutate();
        }}
      >
        <label className="block text-sm font-medium text-ink-700">
          Key
          <Input
            autoFocus
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="marketing.hero.title"
            className="mt-1 font-mono"
          />
          {trimmedKey !== '' && !validKey && (
            <span className="mt-1 block text-xs text-heart">
              Letters, digits, underscores and dots only.
            </span>
          )}
          {warnBundle && (
            // Owner ruling: warn, don't block. A row here does override the
            // bundle at runtime, but regenerating the bundles is the intended
            // way to change these — the override would then be redundant.
            <span className="mt-1 block rounded-lg bg-sun/10 px-2 py-1.5 text-xs text-ink-700">
              This namespace ships in the TypeScript bundle. A row here overrides it at runtime, but
              the bundle stays the source of truth — prefer editing the code for{' '}
              <span className="font-mono">{BUNDLE_PREFIXES.join(' ')}</span> keys.
            </span>
          )}
        </label>

        <label className="block text-sm font-medium text-ink-700">
          Language
          <select
            value={languageCode}
            onChange={(e) => setLanguageCode(e.target.value)}
            className="mt-1 block h-10 w-full rounded-2xl border border-burgundy-500/15 bg-cream-50 px-3 text-sm text-ink-900"
          >
            {languages.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name} ({l.code})
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm font-medium text-ink-700">
          Value
          <Textarea
            dir={dir}
            rows={3}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="mt-1 text-sm"
          />
        </label>

        <label className="block text-sm font-medium text-ink-700">
          Category <span className="font-normal text-ink-500">(optional)</span>
          <Input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="marketing"
            className="mt-1"
          />
        </label>

        <p className="text-xs text-ink-500">
          Saving a key + language that already exists updates it.
        </p>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!validKey || !languageCode || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save string'}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}

// ─── import ─────────────────────────────────────────────────────────────────

function ImportDialog({
  onClose,
  onDone,
  onError,
}: {
  onClose: () => void;
  onDone: (count: number) => void;
  onError: (e: unknown) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = useState<{
    rows: ReturnType<typeof parseTranslationsCsv>['rows'];
    errors: CsvParseError[];
    filename: string;
  } | null>(null);

  const run = useMutation({
    mutationFn: () => importTranslations(parsed?.rows ?? []),
    onSuccess: (r) => onDone(r.imported),
    onError,
  });

  async function handleFile(file: File) {
    const text = await file.text();
    const { rows, errors } = parseTranslationsCsv(text);
    setParsed({ rows, errors, filename: file.name });
  }

  return (
    <DialogShell title="Import translations" onClose={onClose}>
      <p className="text-sm text-ink-700">
        A CSV with a header row of <span className="font-mono text-xs">key,language,value</span> and
        an optional <span className="font-mono text-xs">category</span> column. An export from this
        page imports back unchanged.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
        className="mt-4 block w-full text-sm text-ink-700 file:me-3 file:rounded-xl file:border-0 file:bg-admin-100 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-admin-700"
      />

      {parsed && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-admin-900">
            <span className="font-mono text-xs">{parsed.filename}</span> — {parsed.rows.length} row
            {parsed.rows.length === 1 ? '' : 's'} ready
            {parsed.errors.length > 0 && `, ${parsed.errors.length} skipped`}.
          </p>

          {parsed.errors.length > 0 && (
            <div className="max-h-32 overflow-auto rounded-xl bg-heart/10 p-3">
              <ul className="space-y-1 text-xs text-heart">
                {parsed.errors.slice(0, 20).map((e) => (
                  <li key={`${e.line}-${e.message}`}>
                    Line {e.line}: {e.message}
                  </li>
                ))}
                {parsed.errors.length > 20 && <li>…and {parsed.errors.length - 20} more.</li>}
              </ul>
            </div>
          )}

          {parsed.rows.length > 0 && (
            <div className="max-h-40 overflow-auto rounded-xl border border-burgundy-500/10">
              <table className="w-full text-xs">
                <tbody>
                  {parsed.rows.slice(0, 8).map((r, i) => (
                    <tr
                      key={`${r.translationKey}-${r.languageCode}-${i}`}
                      className="border-b border-burgundy-500/5 last:border-0"
                    >
                      <td className="px-3 py-1.5 font-mono text-admin-700">{r.translationKey}</td>
                      <td className="px-3 py-1.5 uppercase text-ink-500">{r.languageCode}</td>
                      <td
                        className="max-w-48 truncate px-3 py-1.5 text-admin-900"
                        dir={r.languageCode === 'ar' ? 'rtl' : 'ltr'}
                      >
                        {r.translationValue}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsed.rows.length > 8 && (
                <p className="px-3 py-1.5 text-xs text-ink-500">
                  …and {parsed.rows.length - 8} more.
                </p>
              )}
            </div>
          )}

          <p className="text-xs text-ink-500">
            Existing key + language pairs are overwritten. Rows not in the file are left alone.
          </p>
        </div>
      )}

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={!parsed || parsed.rows.length === 0 || run.isPending}
          onClick={() => run.mutate()}
        >
          {run.isPending ? 'Importing…' : `Import ${parsed?.rows.length ?? 0} rows`}
        </Button>
      </div>
    </DialogShell>
  );
}

// ─── delete ─────────────────────────────────────────────────────────────────

function ConfirmDeleteDialog({
  row,
  pending,
  onCancel,
  onConfirm,
}: {
  row: TranslationRow;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const bundleBacked = isBundleKey(row.translationKey);
  return (
    <DialogShell title="Delete string?" onClose={onCancel}>
      <p className="text-sm text-ink-700">
        <span className="font-mono text-xs">{row.translationKey}</span> ·{' '}
        <span className="uppercase">{row.languageCode}</span>
      </p>
      <p className="mt-3 text-sm text-ink-700">
        {bundleBacked
          ? 'This key also ships in the TypeScript bundle, so deleting the override reveals the built-in string. Nothing goes blank.'
          : 'This key exists only here. Deleting it makes the string render EMPTY on the site — there is no fallback to another language.'}
      </p>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={pending} onClick={onConfirm}>
          {pending ? 'Deleting…' : 'Delete string'}
        </Button>
      </div>
    </DialogShell>
  );
}

// ─── shared dialog shell ────────────────────────────────────────────────────

function DialogShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={title} className="max-w-lg bg-white">
        {children}
      </DialogContent>
    </Dialog>
  );
}
